import { IMaintenanceEventFilters, TDayOfWeek } from '@/pages/Calendar/calendar.types'
import FormField from '@/shared/Forms/FormField'
import Modal from '@/shared/Modal'
import AntDateRangePicker from '@/shared/Inputs/DatePickers/AntDateRangePicker'
import DropdownInput from '@/shared/Inputs/DropdownInput'
import { useEffect, useMemo, useState } from 'react'
import SeparateLine from '@/shared/Forms/SeparateLine'
import Button from '@/shared/Buttons/Button'
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { formatDate } from '@/utils/time.utils'

type TReportIntervalOption = 'currentWeek' | 'currentMonth' | 'prevMonth' | 'currentQuarter' | 'custom'

interface IProps {
  open: boolean
  loading?: boolean
  onClose: () => void
  onSubmit: (interval: IMaintenanceEventFilters) => void | Promise<void>
}

const weekStartsOn: TDayOfWeek = 1
const intervalOptions = [
  { title: 'Текущая неделя', value: 'currentWeek' },
  { title: 'Текущий месяц', value: 'currentMonth' },
  { title: 'Прошлый месяц', value: 'prevMonth' },
  { title: 'Текущий квартал', value: 'currentQuarter' },
  { title: 'Свой период', value: 'custom' },
]

const getIntervalByOption = (
  option: TReportIntervalOption,
  customStartTs: number | null,
  customEndTs: number | null
): IMaintenanceEventFilters | null => {
  const now = new Date()

  switch (option) {
    case 'currentWeek':
      return {
        date_gte: startOfWeek(now, { weekStartsOn }),
        date_lte: endOfWeek(now, { weekStartsOn }),
      }
    case 'currentMonth':
      return {
        date_gte: startOfMonth(now),
        date_lte: endOfMonth(now),
      }
    case 'prevMonth': {
      const prevMonth = subMonths(now, 1)
      return {
        date_gte: startOfMonth(prevMonth),
        date_lte: endOfMonth(prevMonth),
      }
    }
    case 'currentQuarter':
      return {
        date_gte: startOfQuarter(now),
        date_lte: endOfQuarter(now),
      }
    case 'custom':
      if (!customStartTs || !customEndTs) return null
      return {
        date_gte: startOfDay(new Date(customStartTs)),
        date_lte: endOfDay(new Date(customEndTs)),
      }
  }
}

const ReportIntervalModal = (props: IProps) => {
  const [intervalType, setIntervalType] = useState<TReportIntervalOption>('currentWeek')
  const [customStartTs, setCustomStartTs] = useState<number | null>(null)
  const [customEndTs, setCustomEndTs] = useState<number | null>(null)

  useEffect(() => {
    if (!props.open) return
    setIntervalType('currentWeek')
    setCustomStartTs(null)
    setCustomEndTs(null)
  }, [props.open])

  const interval = useMemo(() => {
    return getIntervalByOption(intervalType, customStartTs, customEndTs)
  }, [customEndTs, customStartTs, intervalType])

  const intervalText = useMemo(() => {
    if (!interval) return '—'
    return `${formatDate(interval.date_gte, 'dd.MM.yyyy')} — ${formatDate(interval.date_lte, 'dd.MM.yyyy')}`
  }, [interval])

  const handleSubmit = async () => {
    if (!interval) return
    await props.onSubmit(interval)
  }

  return (
    <Modal title="Период отчета" open={props.open} onClose={props.onClose} closeOnClickOutside={false}>
      <div className="flex-lines gap16">
        <FormField label="Период" fullWidth>
          <DropdownInput
            title="Выберите период"
            name="reportInterval"
            options={intervalOptions}
            selectedOption={intervalType}
            onSelect={(value) => setIntervalType(value as TReportIntervalOption)}
            wrapperStyle={{ width: '100%' }}
          />
        </FormField>

        {intervalType === 'custom' && (
          <FormField label="Даты" fullWidth>
            <AntDateRangePicker
              startTs={customStartTs}
              endTs={customEndTs}
              onChange={(start, end) => {
                setCustomStartTs(start)
                setCustomEndTs(end)
              }}
              panelStyle={{ width: '100%' }}
            />
          </FormField>
        )}

        <p className="text-sm text-secondary">Выбранный период: {intervalText}</p>

        <SeparateLine />

        <div className="inline-flex-gap gap8">
          <Button size="default" type="default" text="Отмена" onClick={props.onClose} disabled={props.loading} />
          <Button
            size="default"
            type="primary"
            text="Сформировать отчет"
            onClick={handleSubmit}
            loading={props.loading}
            disabled={!interval}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ReportIntervalModal
